import React from "react";
import styled from "styled-components";
import { C, F, EASE } from "../styles";
import { IconBlackCheck } from "./Icons";
import { BLACK_CLOSES_AT, BLACK_URL } from "../data/calendar";
import { countdownParts } from "../utils/time";
import aluno from "../assets/black/aluno-recorte.webp";
import grao from "../assets/black/grao.webp";

// Segue o design system da campanha Black Total 2026, não a paleta do SOS.

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  ${(p) =>
    !p.$bare &&
    `
    border-radius: 20px;
    overflow: hidden;
    background-color: ${C.graphite};
    background-image: linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
    background-size: 30px 30px;
  `}
`;

const Glow = styled.div.attrs({ "aria-hidden": true })`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(900px 420px at 50% 120%, rgba(255, 82, 39, 0.22), rgba(255, 82, 39, 0) 70%);
`;

export const BlackGrain = styled.div.attrs({ "aria-hidden": true })`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: url(${grao}) 0 0 / 180px 180px repeat;
  mix-blend-mode: overlay;
  opacity: 0.14;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  flex: 1.6 1 420px;
  min-width: min(100%, 300px);
  padding: clamp(20px, 2.6vw, 28px);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Tape = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 18px 7px;
  background: ${C.yellow};
  color: ${C.black};
  transform: rotate(-2.5deg);
  transform-origin: left center;
  clip-path: polygon(0 8%, 3% 0, 97% 4%, 100% 0, 98.5% 50%, 100% 100%, 96% 94%, 4% 100%, 0 96%, 1.5% 50%);
  b {
    font: 400 19px/1 ${F.marker};
    letter-spacing: 0.01em;
  }
  i {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${C.black};
    align-self: center;
  }
  span {
    font: 700 13px/1 ${F.title};
    letter-spacing: 0.04em;
  }
`;

const Headline = styled.span`
  font: 800 clamp(22px, 2.4vw, 28px) / 1.15 ${F.title};
  letter-spacing: -0.04em;
  color: ${C.white};
  text-wrap: balance;
`;

const Benefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 540px;
`;

const Benefit = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  span {
    font: 400 14px/1.5 ${F.body};
    color: rgba(255, 255, 255, 0.88);
    text-wrap: pretty;
  }
  strong {
    color: ${C.white};
    font-weight: 700;
  }
  em {
    font-style: normal;
    color: ${C.yellow};
    font-weight: 700;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 4px;
`;

const Cta = styled.a`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  min-height: 52px;
  font: 700 15px/1 ${F.title};
  background: ${C.orange};
  color: ${C.white};
  border-radius: 12px;
  padding: 0 22px;
  transition: transform 240ms ${EASE}, filter 240ms;
  &:hover {
    color: ${C.white};
    transform: translateY(-2px);
    filter: brightness(1.06);
  }
  &:active {
    filter: brightness(0.94);
  }
`;

const Closes = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  > span {
    font: 600 12px/1.3 ${F.title};
    color: rgba(255, 255, 255, 0.78);
  }
`;

const Units = styled.div`
  display: flex;
  gap: 4px;
`;

const Unit = styled.span`
  min-width: 46px;
  padding: 5px 6px 4px;
  border-radius: 8px;
  background: ${C.graphiteDark};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  b {
    font: 400 18px/1.1 ${F.marker};
    color: ${C.yellow};
    font-variant-numeric: tabular-nums;
  }
  small {
    font: 600 8px/1 ${F.title};
    letter-spacing: 0.08em;
    color: rgba(255, 255, 255, 0.78);
  }
`;

const Photo = styled.div`
  position: relative;
  flex: 0 1 300px;
  min-width: min(100%, 240px);
  min-height: 260px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -40px;
    width: 340px;
    height: 340px;
    transform: translateX(-50%);
    border-radius: 50%;
    pointer-events: none;
    background: radial-gradient(closest-side, rgba(255, 82, 39, 0.55), rgba(255, 82, 39, 0.18) 60%, rgba(255, 82, 39, 0));
  }
  img {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 100%;
    min-height: 260px;
    max-height: 360px;
    width: auto;
    max-width: none;
    display: block;
    filter: contrast(1.08) saturate(1.05);
    -webkit-mask-image: linear-gradient(0deg, transparent 0, #000 22%);
    mask-image: linear-gradient(0deg, transparent 0, #000 22%);
  }
`;

// bare: sem fundo/moldura próprios, para ir dentro de uma faixa Black (transmissão).
// now ausente = sem o countdown do carrinho.
export default function BlackBanner({ now, bare = false }) {
  return (
    <Wrap $bare={bare}>
      <Glow />
      <Content>
        <Tape>
          <b>Black Total</b>
          <i />
          <span>2026</span>
        </Tape>
        <Headline>Tudo que o Aprova tem pra você seguir na frequência até a aprovação.</Headline>
        <Benefits>
          <Benefit>
            <IconBlackCheck />
            <span>
              <strong>Seguro Aprovação:</strong> passou numa universidade pública com o Enem 2026? Parte do que você investiu{" "}
              <em>volta pro seu bolso.</em>
            </span>
          </Benefit>
          <Benefit>
            <IconBlackCheck />
            <span>
              <strong>Aprova Universitário incluso:</strong> a gente continua com você na faculdade.
            </span>
          </Benefit>
        </Benefits>
        <Actions>
          <Cta href={BLACK_URL} target="_blank" rel="noopener noreferrer">
            Quero a Black Total&nbsp;→
          </Cta>
          {now !== undefined && (
          <Closes>
            <span>
              Carrinho fecha
              <br />
              em 27/11
            </span>
            <Units>
              {countdownParts(BLACK_CLOSES_AT, now, false).map((u) => (
                <Unit key={u.l}>
                  <b>{u.v}</b>
                  <small>{u.l}</small>
                </Unit>
              ))}
            </Units>
          </Closes>
          )}
        </Actions>
      </Content>
      <Photo>
        <img src={aluno} alt="Estudante com a camiseta do Aprova Total" />
      </Photo>
      {!bare && <BlackGrain />}
    </Wrap>
  );
}
